/** Drapeaux et sélecteurs de filiale / langue – portage de design/components/bundle.js (Flag, ShapeFlag, WorldGlobe, EntitySwitcher, LocaleSwitcher). */
import React from 'react';
import { cx, h, Icon, useIdStable } from './base';
import { FORMES_PAYS } from './formes-pays';
import { SegmentedControl, type SegmentedOption } from './formulaires';

type Forme = ['v' | 'h', string, string, string] | ['h2', string, string] | ['es' | 'ch' | 'br' | 'gb' | 'us' | 'ca' | 'cz'];

/* Drapeaux simplifiés, couleurs officielles */
const FL: Record<string, Forme> = {
  FR: ['v', '#0055a4', '#ffffff', '#ef4135'], IT: ['v', '#009246', '#ffffff', '#ce2b37'], BE: ['v', '#000000', '#fdda24', '#ef3340'],
  DE: ['h', '#000000', '#dd0000', '#ffce00'], NL: ['h', '#ae1c28', '#ffffff', '#21468b'], PL: ['h2', '#ffffff', '#dc143c'],
  ES: ['es'], CH: ['ch'], BR: ['br'], GB: ['gb'], US: ['us'], CA: ['ca'], CZ: ['cz'],
};

const FEUILLE_ERABLE = 'M12.0 2.6 L12.9 4.3 L13.7 3.9 L13.4 6.6 L14.6 5.5 L15.0 6.4 L16.4 6.1 L15.8 8.1 L16.6 8.5 L14.2 10.4 L14.5 11.2 L12.3 10.9 L12.3 13.2 L11.7 13.2 L11.7 10.9 L9.5 11.2 L9.8 10.4 L7.4 8.5 L8.2 8.1 L7.6 6.1 L9.0 6.4 L9.4 5.5 L10.6 6.6 L10.3 3.9 L11.1 4.3 L12.0 2.6z';

function formesDrapeau(c: string): React.ReactNode[] | null {
  const f = FL[c];
  if (!f) return null;
  switch (f[0]) {
    case 'v': return [h('rect', { key: 1, width: 8, height: 16, fill: f[1] }), h('rect', { key: 2, x: 8, width: 8, height: 16, fill: f[2] }), h('rect', { key: 3, x: 16, width: 8, height: 16, fill: f[3] })];
    case 'h': return [h('rect', { key: 1, width: 24, height: 5.34, fill: f[1] }), h('rect', { key: 2, y: 5.33, width: 24, height: 5.34, fill: f[2] }), h('rect', { key: 3, y: 10.66, width: 24, height: 5.34, fill: f[3] })];
    case 'h2': return [h('rect', { key: 1, width: 24, height: 8, fill: f[1] }), h('rect', { key: 2, y: 8, width: 24, height: 8, fill: f[2] })];
    case 'es': return [h('rect', { key: 1, width: 24, height: 16, fill: '#aa151b' }), h('rect', { key: 2, y: 4, width: 24, height: 8, fill: '#f1bf00' })];
    case 'ch': return [h('rect', { key: 1, width: 24, height: 16, fill: '#da291c' }), h('path', { key: 2, d: 'M10.5 3.5h3v3h3v3h-3v3h-3v-3h-3v-3h3z', fill: '#ffffff' })];
    case 'br': return [h('rect', { key: 1, width: 24, height: 16, fill: '#009c3b' }), h('path', { key: 2, d: 'M12 2 22 8 12 14 2 8z', fill: '#ffdf00' }), h('circle', { key: 3, cx: 12, cy: 8, r: 3.6, fill: '#002776' })];
    case 'gb': return [h('rect', { key: 1, width: 24, height: 16, fill: '#012169' }), h('path', { key: 2, d: 'M0 0l24 16M24 0 0 16', stroke: '#ffffff', strokeWidth: 3.2 }), h('path', { key: 3, d: 'M0 0l24 16M24 0 0 16', stroke: '#c8102e', strokeWidth: 1.2 }), h('path', { key: 4, d: 'M12 0v16M0 8h24', stroke: '#ffffff', strokeWidth: 5 }), h('path', { key: 5, d: 'M12 0v16M0 8h24', stroke: '#c8102e', strokeWidth: 3 })];
    case 'ca': return [h('rect', { key: 1, width: 24, height: 16, fill: '#ffffff' }), h('rect', { key: 2, width: 6, height: 16, fill: '#d80621' }), h('rect', { key: 3, x: 18, width: 6, height: 16, fill: '#d80621' }), h('path', { key: 4, d: FEUILLE_ERABLE, fill: '#d80621' })];
    case 'cz': return [h('rect', { key: 1, width: 24, height: 8, fill: '#ffffff' }), h('rect', { key: 2, y: 8, width: 24, height: 8, fill: '#d7141a' }), h('path', { key: 3, d: 'M0 0L12 8L0 16z', fill: '#11457e' })];
    case 'us': {
      const r: React.ReactNode[] = [h('rect', { key: 0, width: 24, height: 16, fill: '#ffffff' })];
      for (let i = 0; i < 7; i++) r.push(h('rect', { key: 's' + i, y: i * 2.29, width: 24, height: 1.15, fill: '#b22234' }));
      r.push(h('rect', { key: 'c', width: 10, height: 8.6, fill: '#3c3b6e' }));
      return r;
    }
  }
  return null;
}

/**
 * Ajout AGORA (accessibilité) : un drapeau ou un globe sans `label` est décoratif – il accompagne toujours un texte
 * (pays, code, nom de la filiale) ou un bouton déjà nommé – donc masqué aux lecteurs d'écran ; avec `label`, c'est une
 * image nommée. La référence donnait `role="img"` sans nom (violation axe svg-img-alt).
 */
const image = (label?: string) => (label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true });

export interface FlagProps { country?: string; size?: number; label?: string }

export function Flag(p: FlagProps) {
  const c = (p.country || '').toUpperCase();
  const formes = formesDrapeau(c);
  const sz = p.size || 20;
  if (!formes) return h('span', { className: 'ag-flag ag-flag-none', ...image(p.label), style: { width: sz * 1.5, height: sz } }, h(Icon, { name: 'globe', size: sz - 2 }));
  return h('svg', { className: 'ag-flag', width: sz * 1.5, height: sz, viewBox: '0 0 24 16', ...image(p.label), preserveAspectRatio: 'none' }, formes);
}

export interface ShapeFlagProps { country: string; size?: number; label?: string }

/** Drapeau découpé à la forme du pays (contour Natural Earth) ; repli sur Flag sans contour connu. */
export function ShapeFlag(p: ShapeFlagProps) {
  const c = (p.country || '').toUpperCase();
  const sh = FORMES_PAYS[c];
  let formes = formesDrapeau(c);
  const sz = p.size || 22;
  const id = useIdStable('ag-sf');
  if (c === 'CA' && formes) {
    formes = [h('rect', { key: 1, width: 24, height: 16, fill: '#d80621' }), h('path', { key: 2, d: FEUILLE_ERABLE, fill: '#ffffff', transform: 'translate(7 10) scale(0.95 0.68) translate(-12 -8)' })];
  }
  if (!sh || !formes) return h(Flag, { country: c, size: Math.round(sz * 0.72), label: p.label });
  const pad = 0.8;
  const x = sh[1] - pad, y = sh[2] - pad, w = sh[3] + pad * 2, hh = sh[4] + pad * 2;
  return h('svg', { className: 'ag-shapeflag', width: sz, height: sz, viewBox: `${x} ${y} ${w} ${hh}`, ...image(p.label), preserveAspectRatio: 'xMidYMid meet' },
    h('defs', null, h('clipPath', { id }, h('path', { d: sh[0] }))),
    h('g', { clipPath: `url(#${id})` }, h('g', { transform: `translate(${sh[1]} ${sh[2]}) scale(${sh[3] / 24} ${sh[4] / 16})` }, formes)),
    h('path', { d: sh[0], fill: 'none', stroke: 'rgba(0,0,0,0.35)', strokeWidth: 0.6, strokeLinejoin: 'round', vectorEffect: 'non-scaling-stroke' }));
}

export interface WorldGlobeProps { size?: number; label?: string }

/** Globe terrestre – « toutes les filiales ». */
export function WorldGlobe(p: WorldGlobeProps) {
  const id = useIdStable('ag-wg');
  const sz = p.size || 20;
  return h('svg', { className: 'ag-world', width: sz, height: sz, viewBox: '0 0 24 24', ...image(p.label) },
    h('defs', null, h('clipPath', { id }, h('circle', { cx: 12, cy: 12, r: 11 }))),
    h('circle', { cx: 12, cy: 12, r: 11, fill: '#2a7fc4' }),
    h('g', { clipPath: `url(#${id})`, fill: '#5fc49c' },
      h('path', { d: 'M2.5 6.5C4 4 7.5 3 10 4.2c1.2.6.8 2-.2 2.6-1 .7-1.6 1.6-1.5 2.8.1 1.1-.9 1.6-1.8 1.1C5 10 3.6 9.6 2.6 8.6z' }),
      h('path', { d: 'M8.6 12.6c1.6-.3 3 .6 3 2.1 0 1.2-.8 2.1-1.4 3.2-.5 1-.7 2.4-1.6 3.1-.6-.9-.6-2.2-1-3.3-.4-1.2-1.1-2.2-.6-3.4.3-.8.8-1.5 1.6-1.7z' }),
      h('path', { d: 'M13.2 4.6c1.6-.9 3.6-.8 5.2.1 1.6.9 3.4 1.6 4 3.3.3.9-.6 1.6-1.5 1.5-1.2-.2-2.3.3-2.9 1.3-.4.7-1.3.5-1.8-.1-.4-.5-1.2-.6-1.6 0-.3.5.1 1.1.6 1.4 1.4.8 2.6 2 2.5 3.6-.1 1.5-1.2 2.7-2 3.9-.4.6-1.3.5-1.5-.2-.5-1.7-.6-3.5-1.4-5-.4-.7-1.3-1-1.5-1.8-.2-.9.4-1.8.2-2.7-.2-.8-.9-1.4-.9-2.2.1-.9.7-1.6 1.5-2.1z' }),
      h('path', { d: 'M19.5 17.2c.9-.4 2 .1 2.1 1-.4 1-1.4 1.6-2.4 1.4-.6-.7-.4-1.9.3-2.4z' })),
    h('circle', { cx: 12, cy: 12, r: 11, fill: 'none', stroke: 'rgba(0,0,0,0.18)', strokeWidth: 1 }),
    h('path', { d: 'M5.5 5.5a9 9 0 0 1 6-3', fill: 'none', stroke: 'rgba(255,255,255,0.55)', strokeWidth: 1.4, strokeLinecap: 'round' }));
}

export interface EntitySwitcherProps {
  code: string; name: string; country?: string; ariaPrefix?: string; onClick?: () => void;
  /** Ajout AGORA : état de la liste ouverte (aria-expanded). */
  expanded?: boolean;
}

/**
 * Sélecteur de filiale : bâtiment + drapeau à la forme du pays (globe = toutes les filiales) + chevron.
 * Ajout AGORA : transmet sa référence et les props reçues à son bouton, pour servir de déclencheur à un menu Radix
 * (`<Menu trigger={<EntitySwitcher …/>}>`, voir AppHeader.entityMenu) qui pose alors aria-haspopup / aria-expanded.
 */
export const EntitySwitcher = React.forwardRef<HTMLButtonElement, EntitySwitcherProps & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'>>(
  function EntitySwitcher(props, ref) {
    const { code, name, country: pays, ariaPrefix, onClick, expanded, ...bouton } = props;
    const country = pays || (/^[A-Z]{2}\d/.test(code || '') ? code.slice(0, 2) : '');
    const label = name + (code ? ` (${code})` : '');
    return h('button', {
      type: 'button', className: cx('ag-entity', 'ag-glass'), 'aria-haspopup': 'listbox', 'aria-expanded': expanded,
      'aria-label': (ariaPrefix ? ariaPrefix + ' : ' : '') + label, title: label, onClick, ...bouton, ref,
    },
    h(Icon, { name: 'building', size: 16, className: 'ag-entity-bld' }),
    code === 'ALL' ? h(WorldGlobe, { size: 20 }) : h(ShapeFlag, { country, size: 24 }),
    h(Icon, { name: 'chevron', size: 14 }));
  });

export interface LocaleSwitcherProps {
  language?: string; timezone?: string; onClick?: () => void;
  options?: SegmentedOption[]; value?: string; onChange?: (v: string) => void; ariaLabel?: string;
}

/**
 * Langue : contrôle segmenté de codes (jusqu'à 6 langues) ou puce compacte (écran de connexion). Jamais de drapeau.
 * Réglages (« Automatique » + 6 langues, plus de 6 choix) utilise ChoiceList.
 * Ajouts AGORA : la puce transmet sa référence et les props reçues (déclencheur d'un menu Radix) ; son nom accessible
 * reprend le texte affiché après ariaLabel (« Langue : Français ») – WCAG 2.5.3, le nom contient le texte visible.
 */
export const LocaleSwitcher = React.forwardRef<HTMLButtonElement, LocaleSwitcherProps & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick' | 'onChange' | 'value'>>(
  function LocaleSwitcher(props, ref) {
    const { language, timezone, onClick, options, value, onChange, ariaLabel, ...bouton } = props;
    if (options) return h(SegmentedControl, { options, value: value || '', onChange, ariaLabel });
    const affiche = [language, timezone].filter(Boolean).join(' ');
    return h('button', {
      type: 'button', className: 'ag-chip', 'aria-haspopup': 'listbox', 'aria-label': ariaLabel && affiche ? `${ariaLabel} : ${affiche}` : ariaLabel,
      onClick, ...bouton, ref,
    },
    h(Icon, { name: 'globe', size: 16 }), language, timezone ? ' ' : null, timezone ? h('small', null, timezone) : null);
  });
